/**
 * ============================================================================
 * USER / TENANT PAGE: Dynamic Image Studio & Template Designer
 * ============================================================================
 * Purpose:
 *   Visual drag-and-drop designer for creating WhatsApp dynamic image templates
 *   with customizable background, dynamic text layers (fonts, colors, positions,
 *   {{variables}}), and static/dynamic image overlay layers.
 * ============================================================================
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import dynamicImageService from "../../services/dynamicImageService";
import {
  renderTemplateToCanvas,
  extractTemplateVariables,
  generateDataUrl,
  FRAME_PRESETS,
  resolveImageUrl,
} from "./canvasRenderer";

// Standard Google Fonts available in preview
const FONT_OPTIONS = [
  { label: "Inter", value: "Inter" },
  { label: "Poppins", value: "Poppins" },
  { label: "Roboto", value: "Roboto" },
  { label: "Outfit", value: "Outfit" },
  { label: "Montserrat", value: "Montserrat" },
  { label: "Oswald", value: "Oswald" },
  { label: "Pacifico (Script)", value: "Pacifico" },
  { label: "Playfair Display (Serif)", value: "Playfair Display" },
  { label: "Arial", value: "Arial" },
];

// Canvas Aspect Ratio Presets
const CANVAS_PRESETS = [
  { label: "Square (1:1)", width: 1080, height: 1080, icon: "⬛" },
  { label: "Portrait (4:5)", width: 1080, height: 1350, icon: "▯" },
  { label: "Story (9:16)", width: 1080, height: 1920, icon: "📱" },
  { label: "Landscape (16:9)", width: 1200, height: 675, icon: "▭" },
];

// Quick Variable Suggestions
const COMMON_VARIABLES = ["name", "phone", "offer", "code", "date", "company", "discount"];

const DynamicImageEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingOverlay, setUploadingOverlay] = useState(false);

  // Template Data Model
  const [title, setTitle] = useState("New Dynamic Image Template");
  const [category, setCategory] = useState("Marketing");
  const [canvasConfig, setCanvasConfig] = useState({
    width: 1080,
    height: 1080,
    backgroundColor: "#ffffff",
    backgroundImage: "",
    padding: 0,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    isPaddingLinked: true,
    frame: {
      type: "NONE",
      position: "INSIDE", // "INSIDE" = in the image (0 outside padding), "OUTSIDE" = outside of the image
      width: 24, // border thickness
      scaleX: 100, // frame width scale %
      scaleY: 100, // frame height scale %
      offsetX: 0, // frame X adjustment px
      offsetY: 0, // frame Y adjustment px
      color: "#b91c1c",
      accentColor: "#f59e0b",
      padding: 0,
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      customUrl: "",
    },
  });
  const [layers, setLayers] = useState([
    {
      id: "layer_title",
      type: "text",
      name: "Greeting Title",
      text: "Hello {{name}}!",
      x: 100,
      y: 120,
      fontSize: 64,
      fontFamily: "Outfit",
      fontWeight: 800,
      color: "#ffffff",
      textAlign: "left",
      isItalic: false,
      maxWidth: 880,
      lineHeight: 80,
      shadowColor: "rgba(0,0,0,0.4)",
      shadowBlur: 10,
      shadowOffsetX: 2,
      shadowOffsetY: 4,
      visible: true,
    },
    {
      id: "layer_body",
      type: "text",
      name: "Offer Message",
      text: "Get {{offer}} Off on your next order with code {{code}}.",
      x: 100,
      y: 220,
      fontSize: 36,
      fontFamily: "Inter",
      fontWeight: 500,
      color: "#94a3b8",
      textAlign: "left",
      isItalic: false,
      maxWidth: 880,
      lineHeight: 50,
      visible: true,
    },
  ]);

  const [selectedLayerId, setSelectedLayerId] = useState("layer_title");
  const [zoom, setZoom] = useState(0.5); // Default zoom for 1080x1080 workspace
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Test Variables Modal State
  const [showTestModal, setShowTestModal] = useState(false);
  const [testValues, setTestValues] = useState({
    name: "John Doe",
    offer: "30% OFF",
    code: "SAVE30",
    company: "Digiforest",
  });
  const [testPreviewUrl, setTestPreviewUrl] = useState("");

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const bgFileInputRef = useRef(null);
  const overlayFileInputRef = useRef(null);
  const replaceFileInputRef = useRef(null);
  const customFrameFileInputRef = useRef(null);

  // Active selected layer reference
  const selectedLayer = useMemo(() => {
    return layers.find((l) => l.id === selectedLayerId) || null;
  }, [layers, selectedLayerId]);

  // Detected variables from layers
  const detectedVariables = useMemo(() => {
    return extractTemplateVariables({ layers });
  }, [layers]);

  // Load existing template if editing
  useEffect(() => {
    if (isEditing) {
      loadTemplate(id);
    }
  }, [id, isEditing]);

  const loadTemplate = async (templateId) => {
    try {
      setLoading(true);
      const res = await dynamicImageService.getTemplateById(templateId);
      const t = res?.data?.data || res?.data || res;
      if (t && (t.str_title || t.str_template_name || t.canvas || t.layers)) {
        setTitle(t.str_title || t.str_template_name || "Dynamic Template");
        const padTop = Number(t.canvas?.paddingTop ?? t.canvas?.frame?.paddingTop ?? t.canvas?.padding ?? 0);
        const padRight = Number(t.canvas?.paddingRight ?? t.canvas?.frame?.paddingRight ?? t.canvas?.padding ?? 0);
        const padBottom = Number(t.canvas?.paddingBottom ?? t.canvas?.frame?.paddingBottom ?? t.canvas?.padding ?? 0);
        const padLeft = Number(t.canvas?.paddingLeft ?? t.canvas?.frame?.paddingLeft ?? t.canvas?.padding ?? 0);
        const isLinked = padTop === padRight && padTop === padBottom && padTop === padLeft;

        const frameData = {
          type: t.str_frame_type || t.canvas?.frame?.type || "NONE",
          position: t.str_frame_position || t.canvas?.frame?.position || "INSIDE",
          width: Number(t.canvas?.frame?.width) || 24,
          scaleX: Number(t.canvas?.frame?.scaleX ?? 100),
          scaleY: Number(t.canvas?.frame?.scaleY ?? 100),
          isScaleLinked: t.canvas?.frame?.isScaleLinked ?? true,
          offsetX: Number(t.canvas?.frame?.offsetX ?? 0),
          offsetY: Number(t.canvas?.frame?.offsetY ?? 0),
          color: t.canvas?.frame?.color || t.str_main_color || "#b91c1c",
          accentColor: t.canvas?.frame?.accentColor || t.str_accent_color || "#f59e0b",
          padding: t.canvas?.padding ?? t.canvas?.frame?.padding ?? 0,
          paddingTop: padTop,
          paddingRight: padRight,
          paddingBottom: padBottom,
          paddingLeft: padLeft,
          customUrl: resolveImageUrl(t.canvas?.frame?.customUrl || t.str_frame_custom_url || ""),
        };

        const rawBg = t.canvas?.backgroundImage || t.str_bg_image_url || "";
        const bgImgResolved = resolveImageUrl(rawBg);

        setCanvasConfig({
          width: t.canvas?.width || t.num_width || 1080,
          height: t.canvas?.height || t.num_height || 1080,
          backgroundColor: t.canvas?.backgroundColor || t.str_bg_color || "#ffffff",
          backgroundImage: bgImgResolved,
          padding: t.canvas?.padding ?? frameData.padding ?? 0,
          paddingTop: padTop,
          paddingRight: padRight,
          paddingBottom: padBottom,
          paddingLeft: padLeft,
          isPaddingLinked: isLinked,
          frame: frameData,
        });
        if (Array.isArray(t.layers) && t.layers.length > 0) {
          const resolvedLayers = t.layers.map((l) =>
            l.type === "image" && l.url ? { ...l, url: resolveImageUrl(l.url) } : l
          );
          setLayers(resolvedLayers);
          setSelectedLayerId(resolvedLayers[0]?.id || null);
        } else if (Array.isArray(t.arr_text_boxes) && t.arr_text_boxes.length > 0) {
          const mapped = t.arr_text_boxes.map((b, i) => ({
            id: b.id || `layer_${i}`,
            type: "text",
            name: `Text ${i + 1}`,
            text: b.text || "",
            x: Math.round(((b.x || 50) / 100) * (t.num_width || 1080)),
            y: Math.round(((b.y || 50) / 100) * (t.num_height || 1080)),
            fontSize: b.fontSize || 42,
            fontFamily: b.fontFamily || "Poppins",
            fontWeight: b.fontWeight === "bold" ? 700 : 500,
            color: b.color || "#ffffff",
            textAlign: b.textAlign || "left",
            visible: true,
          }));
          setLayers(mapped);
          setSelectedLayerId(mapped[0]?.id);
        }
      }
    } catch (err) {
      console.error("Failed to load template:", err);
      toast.error("Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  // Re-render canvas whenever template, layers, or selectedLayer change
  useEffect(() => {
    if (!canvasRef.current) return;
    renderTemplateToCanvas(canvasRef.current, {
      canvas: canvasConfig,
      layers: layers,
    }, {});
  }, [canvasConfig, layers]);

  // Auto adjust zoom on mount or canvas resize
  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth - 80;
      const targetZoom = Math.min(0.65, Math.max(0.2, (containerWidth / canvasConfig.width) * 0.85));
      setZoom(Number(targetZoom.toFixed(2)));
    }
  }, [canvasConfig.width]);

  // Update selected layer property helper
  const updateSelectedLayer = (key, value) => {
    if (!selectedLayerId) return;
    setLayers((prev) =>
      prev.map((layer) => {
        if (layer.id === selectedLayerId) {
          return { ...layer, [key]: value };
        }
        return layer;
      })
    );
  };

  // Add new Text Layer
  const handleAddTextLayer = () => {
    const newId = `layer_text_${Date.now()}`;
    const newLayer = {
      id: newId,
      type: "text",
      name: `Text ${layers.length + 1}`,
      text: "Dynamic Text {{name}}",
      x: 120,
      y: 120 + layers.length * 40,
      fontSize: 48,
      fontFamily: "Inter",
      fontWeight: 700,
      color: "#ffffff",
      textAlign: "left",
      isItalic: false,
      maxWidth: canvasConfig.width - 240,
      lineHeight: 60,
      visible: true,
    };
    setLayers((prev) => [...prev, newLayer]);
    setSelectedLayerId(newId);
    toast.success("Text layer added");
  };

  // Add new Image Layer
  const handleAddImageLayer = (imageUrl = "") => {
    const newId = `layer_img_${Date.now()}`;
    const newLayer = {
      id: newId,
      type: "image",
      name: `Image ${layers.length + 1}`,
      url: imageUrl || "{{qr_code}}",
      x: Math.round((canvasConfig.width - 300) / 2),
      y: Math.round((canvasConfig.height - 300) / 2),
      width: 300,
      height: 300,
      borderRadius: 12,
      opacity: 1,
      visible: true,
    };
    setLayers((prev) => [...prev, newLayer]);
    setSelectedLayerId(newId);
    toast.success("Image layer added to canvas");
  };

  // Add Dynamic Variable Placeholder Image (e.g. QR Code, Profile Pic)
  const handleAddDynamicPlaceholderImage = (varName = "qr_code") => {
    const newId = `layer_img_${Date.now()}`;
    const newLayer = {
      id: newId,
      type: "image",
      name: `Dynamic {{${varName}}}`,
      url: `{{${varName}}}`,
      x: Math.round((canvasConfig.width - 250) / 2),
      y: Math.round((canvasConfig.height - 250) / 2),
      width: 250,
      height: 250,
      borderRadius: 12,
      opacity: 1,
      visible: true,
    };
    setLayers((prev) => [...prev, newLayer]);
    setSelectedLayerId(newId);
    toast.success(`Dynamic image {{${varName}}} added`);
  };

  // Duplicate layer
  const handleDuplicateLayer = () => {
    if (!selectedLayer) return;
    const newId = `layer_copy_${Date.now()}`;
    const copy = {
      ...selectedLayer,
      id: newId,
      name: `${selectedLayer.name} (Copy)`,
      x: (selectedLayer.x || 0) + 30,
      y: (selectedLayer.y || 0) + 30,
    };
    setLayers((prev) => [...prev, copy]);
    setSelectedLayerId(newId);
    toast.success("Layer duplicated");
  };

  // Delete layer
  const handleDeleteLayer = (idToDelete = selectedLayerId) => {
    if (!idToDelete) return;
    setLayers((prev) => prev.filter((l) => l.id !== idToDelete));
    if (selectedLayerId === idToDelete) {
      setSelectedLayerId(layers[0]?.id || null);
    }
    toast.success("Layer deleted");
  };

  // Move layer order
  const handleMoveLayer = (direction) => {
    const index = layers.findIndex((l) => l.id === selectedLayerId);
    if (index < 0) return;
    const newIndex = direction === "up" ? index + 1 : index - 1;
    if (newIndex < 0 || newIndex >= layers.length) return;

    const reordered = [...layers];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(newIndex, 0, moved);
    setLayers(reordered);
  };

  // Upload Background Image with immediate local preview
  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 1. Immediately render on canvas via local object URL
    const localUrl = URL.createObjectURL(file);
    setCanvasConfig((prev) => ({
      ...prev,
      backgroundImage: localUrl,
    }));
    toast.success("Background applied to canvas!");

    // 2. Upload to server in background
    try {
      setUploadingBg(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset", file);
      const res = await dynamicImageService.uploadAsset(formData);
      const uploadedUrl =
        res?.url || res?.fileUrl || res?.data?.url || res?.data?.fileUrl;
      if (uploadedUrl) {
        const resolved = resolveImageUrl(uploadedUrl);
        setCanvasConfig((prev) => ({
          ...prev,
          backgroundImage: resolved,
        }));
      }
    } catch (err) {
      console.error("Bg upload error:", err);
    } finally {
      setUploadingBg(false);
      if (bgFileInputRef.current) bgFileInputRef.current.value = "";
    }
  };

  // Upload Overlay Image with immediate local preview on canvas
  const handleOverlayUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 1. Immediately create local preview & add image layer
    const localUrl = URL.createObjectURL(file);
    const newId = `layer_img_${Date.now()}`;
    const newLayer = {
      id: newId,
      type: "image",
      name: file.name ? file.name.slice(0, 20) : `Image ${layers.length + 1}`,
      url: localUrl,
      x: Math.round((canvasConfig.width - 320) / 2),
      y: Math.round((canvasConfig.height - 320) / 2),
      width: 320,
      height: 320,
      borderRadius: 0,
      opacity: 1,
      visible: true,
    };

    setLayers((prev) => [...prev, newLayer]);
    setSelectedLayerId(newId);
    toast.success("Image layer added to canvas!");

    // 2. Upload to server in background
    try {
      setUploadingOverlay(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset", file);
      const res = await dynamicImageService.uploadAsset(formData);
      const uploadedUrl =
        res?.url || res?.fileUrl || res?.data?.url || res?.data?.fileUrl;

      if (uploadedUrl) {
        const resolved = resolveImageUrl(uploadedUrl);
        setLayers((prev) =>
          prev.map((l) => (l.id === newId ? { ...l, url: resolved } : l))
        );
      }
    } catch (err) {
      console.error("Image upload error:", err);
      // Keep local preview intact
    } finally {
      setUploadingOverlay(false);
      if (overlayFileInputRef.current) overlayFileInputRef.current.value = "";
    }
  };

  // Replace existing selected image layer
  const handleReplaceImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedLayer || selectedLayer.type !== "image") return;

    const localUrl = URL.createObjectURL(file);
    const targetId = selectedLayer.id;
    updateSelectedLayer("url", localUrl);
    toast.success("Image replaced on canvas!");

    try {
      setUploadingOverlay(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset", file);
      const res = await dynamicImageService.uploadAsset(formData);
      const uploadedUrl =
        res?.url || res?.fileUrl || res?.data?.url || res?.data?.fileUrl;

      if (uploadedUrl) {
        const resolved = resolveImageUrl(uploadedUrl);
        setLayers((prev) =>
          prev.map((l) => (l.id === targetId ? { ...l, url: resolved } : l))
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUploadingOverlay(false);
      if (replaceFileInputRef.current) replaceFileInputRef.current.value = "";
    }
  };

  // Canvas Mouse Dragging & Hit-Testing
  const handleCanvasMouseDown = (e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const clickX = (e.clientX - rect.left) / zoom;
    const clickY = (e.clientY - rect.top) / zoom;

    // Detect if user clicked directly on any visible layer (topmost first)
    const clickedLayer = [...layers].reverse().find((layer) => {
      if (layer.visible === false) return false;
      const lx = Number(layer.x) || 0;
      const ly = Number(layer.y) || 0;
      const lw = layer.type === "text" ? (Number(layer.maxWidth) || 400) : (Number(layer.width) || 200);
      const lh = layer.type === "text" ? (Number(layer.lineHeight) || 60) * 1.5 : (Number(layer.height) || 200);
      return clickX >= lx && clickX <= lx + lw && clickY >= ly && clickY <= ly + lh;
    });

    if (clickedLayer) {
      if (clickedLayer.id !== selectedLayerId) {
        setSelectedLayerId(clickedLayer.id);
      }
      setIsDragging(true);
      setDragStart({
        x: clickX - (clickedLayer.x || 0),
        y: clickY - (clickedLayer.y || 0),
      });
    } else {
      // Clicked on background: deselect active layer to inspect canvas & frames
      setSelectedLayerId(null);
    }
  };

  // Global mousemove/mouseup listener while dragging to prevent getting stuck
  useEffect(() => {
    if (!isDragging) return;
    const onWindowMove = (e) => {
      if (!canvasRef.current || !selectedLayer) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const currentX = (e.clientX - rect.left) / zoom;
      const currentY = (e.clientY - rect.top) / zoom;
      const newX = Math.round(currentX - dragStart.x);
      const newY = Math.round(currentY - dragStart.y);
      updateSelectedLayer("x", newX);
      updateSelectedLayer("y", newY);
    };
    const onWindowUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", onWindowMove);
    window.addEventListener("mouseup", onWindowUp);
    return () => {
      window.removeEventListener("mousemove", onWindowMove);
      window.removeEventListener("mouseup", onWindowUp);
    };
  }, [isDragging, dragStart, zoom, selectedLayer]);

  const handleCanvasMouseMove = (e) => {
    if (!isDragging || !selectedLayer || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const currentX = (e.clientX - rect.left) / zoom;
    const currentY = (e.clientY - rect.top) / zoom;
    updateSelectedLayer("x", Math.round(currentX - dragStart.x));
    updateSelectedLayer("y", Math.round(currentY - dragStart.y));
  };

  const handleCanvasMouseUp = () => {
    setIsDragging(false);
  };

  // Insert Variable Token into active text layer
  const handleInsertVariable = (varName) => {
    if (!selectedLayer || selectedLayer.type !== "text") {
      toast.error("Select a text layer first to insert variable");
      return;
    }
    const token = `{{${varName}}}`;
    const newText = (selectedLayer.text || "") + " " + token;
    updateSelectedLayer("text", newText);
    toast.success(`Inserted ${token}`);
  };

  // Open Test Variables Live Modal
  const handleOpenTestModal = async () => {
    // Populate any new variables into test values
    const currentTestVals = { ...testValues };
    detectedVariables.forEach((v) => {
      if (!currentTestVals[v]) {
        currentTestVals[v] = `Sample ${v.toUpperCase()}`;
      }
    });
    setTestValues(currentTestVals);

    const previewUrl = await generateDataUrl(
      { canvas: canvasConfig, layers },
      currentTestVals
    );
    setTestPreviewUrl(previewUrl);
    setShowTestModal(true);
  };

  const handleUpdateTestVal = async (key, val) => {
    const updated = { ...testValues, [key]: val };
    setTestValues(updated);
    const previewUrl = await generateDataUrl(
      { canvas: canvasConfig, layers },
      updated
    );
    setTestPreviewUrl(previewUrl);
  };

  // Frame & Margin / Padding Management Helpers
  const updateFrameConfig = (key, value) => {
    setCanvasConfig((prev) => ({
      ...prev,
      frame: {
        ...(prev.frame || {
          type: "NONE",
          position: "INSIDE",
          width: 24,
          scaleX: 100,
          scaleY: 100,
          isScaleLinked: true,
          offsetX: 0,
          offsetY: 0,
          color: "#b91c1c",
          accentColor: "#f59e0b",
          padding: 0,
          paddingTop: 0,
          paddingRight: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          customUrl: "",
        }),
        [key]: value,
      },
    }));
  };

  const handleSelectFrame = (frameId) => {
    const preset = FRAME_PRESETS.find((f) => f.id === frameId);
    setCanvasConfig((prev) => {
      const curFrame = prev.frame || {};
      const newCustomUrl = preset?.defaultAsset || (frameId === "CUSTOM_PNG" ? curFrame.customUrl : "");
      return {
        ...prev,
        frame: {
          ...curFrame,
          type: frameId,
          customUrl: newCustomUrl,
        },
      };
    });

    if (frameId === "CUSTOM_PNG" && !canvasConfig.frame?.customUrl) {
      customFrameFileInputRef.current?.click();
    }
    const label = preset?.label || frameId;
    toast.success(`Selected frame: ${label}`);
  };

  // 4-Sided Margin/Padding setters
  const handleSetSidePadding = (side, val) => {
    const num = Math.max(0, Math.min(300, Number(val) || 0));
    setCanvasConfig((prev) => {
      if (prev.isPaddingLinked) {
        return {
          ...prev,
          padding: num,
          paddingTop: num,
          paddingRight: num,
          paddingBottom: num,
          paddingLeft: num,
          frame: {
            ...(prev.frame || {}),
            padding: num,
            paddingTop: num,
            paddingRight: num,
            paddingBottom: num,
            paddingLeft: num,
          },
        };
      }
      return {
        ...prev,
        [side]: num,
        frame: {
          ...(prev.frame || {}),
          [side]: num,
        },
      };
    });
  };

  const handleTogglePaddingLinked = () => {
    setCanvasConfig((prev) => ({
      ...prev,
      isPaddingLinked: !prev.isPaddingLinked,
    }));
  };

  const handleApplyMarginPreset = (presetKey) => {
    let p = { top: 0, right: 0, bottom: 0, left: 0, linked: false };
    if (presetKey === "none") {
      p = { top: 0, right: 0, bottom: 0, left: 0, linked: true };
    } else if (presetKey === "uniform24") {
      p = { top: 24, right: 24, bottom: 24, left: 24, linked: true };
    } else if (presetKey === "uniform48") {
      p = { top: 48, right: 48, bottom: 48, left: 48, linked: true };
    } else if (presetKey === "polaroid") {
      p = { top: 24, right: 24, bottom: 140, left: 24, linked: false };
    } else if (presetKey === "banner") {
      p = { top: 20, right: 20, bottom: 110, left: 20, linked: false };
    }

    setCanvasConfig((prev) => ({
      ...prev,
      padding: p.top,
      paddingTop: p.top,
      paddingRight: p.right,
      paddingBottom: p.bottom,
      paddingLeft: p.left,
      isPaddingLinked: p.linked,
      frame: {
        ...(prev.frame || {}),
        padding: p.top,
        paddingTop: p.top,
        paddingRight: p.right,
        paddingBottom: p.bottom,
        paddingLeft: p.left,
      },
    }));
    toast.success(`Margin preset applied!`);
  };

  const handleRemoveOutsidePadding = () => {
    handleApplyMarginPreset("none");
  };

  const handleFramePositionChange = (position) => {
    setCanvasConfig((prev) => ({
      ...prev,
      padding: position === "INSIDE" ? 0 : (prev.padding || 0),
      paddingTop: position === "INSIDE" ? 0 : (prev.paddingTop || 0),
      paddingRight: position === "INSIDE" ? 0 : (prev.paddingRight || 0),
      paddingBottom: position === "INSIDE" ? 0 : (prev.paddingBottom || 0),
      paddingLeft: position === "INSIDE" ? 0 : (prev.paddingLeft || 0),
      frame: {
        ...(prev.frame || {}),
        position,
        padding: position === "INSIDE" ? 0 : (prev.frame?.padding || 0),
        paddingTop: position === "INSIDE" ? 0 : (prev.frame?.paddingTop || 0),
        paddingRight: position === "INSIDE" ? 0 : (prev.frame?.paddingRight || 0),
        paddingBottom: position === "INSIDE" ? 0 : (prev.frame?.paddingBottom || 0),
        paddingLeft: position === "INSIDE" ? 0 : (prev.frame?.paddingLeft || 0),
      },
    }));
    toast.success(
      `Frame set ${position === "INSIDE" ? "in the image (0 outside padding)" : "outside of the image"}`
    );
  };

  // Frame Dimension & Position Adjustment Handlers
  const handleSetFrameScale = (axis, val) => {
    const num = Math.max(10, Math.min(250, Number(val) || 100));
    setCanvasConfig((prev) => {
      const curFrame = prev.frame || {};
      const isLinked = curFrame.isScaleLinked !== false;
      if (axis === "x") {
        return {
          ...prev,
          frame: {
            ...curFrame,
            scaleX: num,
            ...(isLinked ? { scaleY: num } : {}),
          },
        };
      } else {
        return {
          ...prev,
          frame: {
            ...curFrame,
            scaleY: num,
            ...(isLinked ? { scaleX: num } : {}),
          },
        };
      }
    });
  };

  const handleToggleScaleLinked = () => {
    setCanvasConfig((prev) => ({
      ...prev,
      frame: {
        ...(prev.frame || {}),
        isScaleLinked: prev.frame?.isScaleLinked === false ? true : false,
      },
    }));
  };

  const handleSetFrameOffset = (axis, val) => {
    const num = Math.max(-500, Math.min(500, Number(val) || 0));
    updateFrameConfig(axis === "x" ? "offsetX" : "offsetY", num);
  };

  const handleCenterFrame = (axis = "both") => {
    setCanvasConfig((prev) => ({
      ...prev,
      frame: {
        ...(prev.frame || {}),
        ...(axis === "x" || axis === "both" ? { offsetX: 0 } : {}),
        ...(axis === "y" || axis === "both" ? { offsetY: 0 } : {}),
      },
    }));
    toast.success("Frame centered");
  };

  const handleResetFrameAdjustments = () => {
    setCanvasConfig((prev) => ({
      ...prev,
      frame: {
        ...(prev.frame || {}),
        scaleX: 100,
        scaleY: 100,
        offsetX: 0,
        offsetY: 0,
      },
    }));
    toast.success("Frame size & position reset to default");
  };

  const handleCustomFrameUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);
    updateFrameConfig("type", "CUSTOM_PNG");
    updateFrameConfig("customUrl", localUrl);
    toast.success("Custom frame applied to canvas!");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("asset", file);
      const res = await dynamicImageService.uploadAsset(formData);
      const uploadedUrl = res?.url || res?.fileUrl || res?.data?.url;
      if (uploadedUrl) {
        const resolved = resolveImageUrl(uploadedUrl);
        updateFrameConfig("customUrl", resolved);
      }
    } catch (err) {
      console.error("Frame upload error:", err);
    } finally {
      if (customFrameFileInputRef.current) customFrameFileInputRef.current.value = "";
    }
  };

  // Save Dynamic Template
  const handleSaveTemplate = async () => {
    if (!title.trim()) {
      toast.error("Please enter a template title");
      return;
    }

    try {
      setSaving(true);

      // Generate preview thumbnail
      const previewDataUrl = await generateDataUrl(
        { canvas: canvasConfig, layers },
        testValues
      );

      const payload = {
        str_template_name: title.trim(),
        str_title: title.trim(),
        str_category: category,
        canvas: canvasConfig,
        layers: layers,
        num_width: canvasConfig.width,
        num_height: canvasConfig.height,
        str_bg_color: canvasConfig.backgroundColor,
        str_bg_image_url: canvasConfig.backgroundImage || "",
        str_frame_type: canvasConfig.frame?.type || "NONE",
        str_frame_position: canvasConfig.frame?.position || "INSIDE",
        str_frame_custom_url: canvasConfig.frame?.customUrl || "",
        str_main_color: canvasConfig.frame?.color || "#b91c1c",
        str_accent_color: canvasConfig.frame?.accentColor || "#f59e0b",
        str_preview_image_url: previewDataUrl,
        arr_variables: detectedVariables,
      };

      if (isEditing) {
        await dynamicImageService.updateTemplate(id, payload);
        toast.success("Dynamic Image Template updated successfully!");
      } else {
        await dynamicImageService.createTemplate(payload);
        toast.success("Dynamic Image Template created successfully!");
      }

      navigate("/workspace/dynamic-images/templates");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900 text-white">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#3a57e8] border-r-transparent"></div>
          <p className="mt-2 text-sm text-slate-400">Loading Dynamic Image Studio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-[#0f172a] text-slate-200 overflow-hidden">
      {/* Top Navigation Bar */}
      <div className="h-14 border-b border-slate-800 bg-[#1e293b]/80 backdrop-blur px-4 flex items-center justify-between z-20">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/workspace/dynamic-images/templates")}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
            title="Back to Templates"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xl">🎨</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-transparent border-b border-transparent hover:border-slate-700 focus:border-[#3a57e8] focus:outline-none px-2 py-0.5 text-base font-semibold text-white tracking-wide transition"
              placeholder="Template Title..."
            />
          </div>
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-medium">
            {canvasConfig.width} × {canvasConfig.height}px
          </span>
        </div>

        {/* Center Canvas Preset Selector */}
        <div className="hidden lg:flex items-center gap-1 bg-slate-900/60 p-1 rounded-xl border border-slate-800">
          {CANVAS_PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => {
                setCanvasConfig((prev) => ({
                  ...prev,
                  width: p.width,
                  height: p.height,
                }));
              }}
              className={`px-3 py-1 text-xs rounded-lg font-medium transition flex items-center gap-1.5 ${
                canvasConfig.width === p.width && canvasConfig.height === p.height
                  ? "bg-[#3a57e8] text-white shadow-sm"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
            >
              <span>{p.icon}</span>
              <span>{p.label}</span>
            </button>
          ))}
        </div>

        {/* Right Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenTestModal}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600 hover:text-white border border-emerald-500/30 transition shadow-sm"
          >
            <span>⚡</span>
            <span>Test Variables ({detectedVariables.length})</span>
          </button>

          <button
            onClick={handleSaveTemplate}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg bg-[#3a57e8] text-white hover:bg-blue-600 transition shadow-md shadow-[#3a57e8]/30 disabled:opacity-60"
          >
            {saving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Save Template</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main 3-Column Studio Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT COLUMN: Tooling & Assets */}
        <div className="w-64 border-r border-slate-800 bg-[#1e293b]/50 p-4 flex flex-col gap-5 overflow-y-auto">
          {/* Quick Add Elements */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2.5">
              Add Elements
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleAddTextLayer}
                className="flex flex-col items-center justify-center p-3 rounded-xl bg-slate-800/80 hover:bg-[#3a57e8] text-slate-300 hover:text-white border border-slate-700/60 hover:border-[#3a57e8] transition shadow-sm group"
              >
                <span className="text-2xl font-bold group-hover:scale-110 transition">T</span>
                <span className="text-xs font-medium mt-1">Dynamic Text</span>
              </button>

              <button
                onClick={() => overlayFileInputRef.current?.click()}
                disabled={uploadingOverlay}
                className="flex flex-col items-center justify-center p-3 rounded-xl bg-slate-800/80 hover:bg-[#3a57e8] text-slate-300 hover:text-white border border-slate-700/60 hover:border-[#3a57e8] transition shadow-sm group"
              >
                <span className="text-2xl group-hover:scale-110 transition">🖼️</span>
                <span className="text-xs font-medium mt-1">
                  {uploadingOverlay ? "Uploading..." : "Add Image"}
                </span>
              </button>
              <input
                ref={overlayFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleOverlayUpload}
                className="hidden"
              />
            </div>

            <button
              onClick={() => handleAddDynamicPlaceholderImage("qr_code")}
              className="w-full mt-2 py-1.5 px-2 rounded-lg bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 text-[11px] text-blue-400 hover:text-blue-300 font-mono transition text-center flex items-center justify-center gap-1.5"
            >
              <span>+ Add {"{{qr_code}}"} Placeholder</span>
            </button>
          </div>

          {/* Background Settings */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2.5">
              Canvas Background
            </h3>
            <div className="space-y-3 bg-slate-900/60 p-3 rounded-xl border border-slate-800">
              <div>
                <label className="text-[11px] text-slate-400 block mb-1">Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={canvasConfig.backgroundColor}
                    onChange={(e) =>
                      setCanvasConfig((prev) => ({
                        ...prev,
                        backgroundColor: e.target.value,
                      }))
                    }
                    className="w-8 h-8 rounded-lg cursor-pointer bg-transparent border-0"
                  />
                  <input
                    type="text"
                    value={canvasConfig.backgroundColor}
                    onChange={(e) =>
                      setCanvasConfig((prev) => ({
                        ...prev,
                        backgroundColor: e.target.value,
                      }))
                    }
                    className="flex-1 text-xs bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-white font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] text-slate-400 block mb-1">Background Image</label>
                {canvasConfig.backgroundImage ? (
                  <div className="relative group rounded-lg overflow-hidden border border-slate-700">
                    <img
                      src={resolveImageUrl(canvasConfig.backgroundImage)}
                      alt="Background"
                      className="w-full h-20 object-cover"
                      onError={(e) => {
                        const cur = e.target.src || "";
                        if (cur.includes("apis.digiforest.in")) {
                          e.target.src = cur.replace("https://apis.digiforest.in", "http://localhost:8005");
                        } else {
                          e.target.onerror = null;
                        }
                      }}
                    />
                    <button
                      onClick={() =>
                        setCanvasConfig((prev) => ({ ...prev, backgroundImage: "" }))
                      }
                      className="absolute top-1 right-1 bg-red-600/80 hover:bg-red-600 text-white rounded p-1 text-[10px] transition"
                      title="Remove background image"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => bgFileInputRef.current?.click()}
                    disabled={uploadingBg}
                    className="w-full py-2 px-3 border border-dashed border-slate-700 hover:border-[#3a57e8] rounded-lg text-xs text-slate-400 hover:text-white transition text-center flex items-center justify-center gap-1.5"
                  >
                    <span>📷</span>
                    <span>{uploadingBg ? "Uploading..." : "Upload Background"}</span>
                  </button>
                )}
                <input
                  ref={bgFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleBgUpload}
                  className="hidden"
                />
              </div>

              {/* 4-Sided Margin & Padding Controls */}
              <div className="pt-2.5 border-t border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-semibold text-slate-300">Margin / Padding</span>
                    <button
                      type="button"
                      onClick={handleTogglePaddingLinked}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition flex items-center gap-1 ${
                        canvasConfig.isPaddingLinked !== false
                          ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                          : "bg-slate-800 text-slate-400 hover:text-white"
                      }`}
                      title={canvasConfig.isPaddingLinked !== false ? "Sides Linked (Click to Unlink)" : "Sides Independent (Click to Link)"}
                    >
                      <span>{canvasConfig.isPaddingLinked !== false ? "🔗 Linked" : "🔓 Independent"}</span>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveOutsidePadding}
                    className={`text-[10px] px-2 py-0.5 rounded transition font-medium ${
                      (canvasConfig.paddingTop || 0) === 0 &&
                      (canvasConfig.paddingRight || 0) === 0 &&
                      (canvasConfig.paddingBottom || 0) === 0 &&
                      (canvasConfig.paddingLeft || 0) === 0
                        ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold"
                        : "bg-slate-800 hover:bg-slate-700 text-slate-300"
                    }`}
                    title="Set all margins & padding to 0px (Full bleed)"
                  >
                    {(canvasConfig.paddingTop || 0) === 0 &&
                    (canvasConfig.paddingRight || 0) === 0 &&
                    (canvasConfig.paddingBottom || 0) === 0 &&
                    (canvasConfig.paddingLeft || 0) === 0
                      ? "✓ 0px Bleed"
                      : "✕ 0px All"}
                  </button>
                </div>

                {/* Linked Single Slider or Independent 4-Sides */}
                {canvasConfig.isPaddingLinked !== false ? (
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
                      <span>All Sides: {canvasConfig.padding || 0}px</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={250}
                          value={canvasConfig.padding || 0}
                          onChange={(e) => handleSetSidePadding("paddingTop", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-[11px] font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">px</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={180}
                      value={canvasConfig.padding || 0}
                      onChange={(e) => handleSetSidePadding("paddingTop", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                ) : (
                  <div className="space-y-2 pt-1">
                    {/* 4-Box Visual Grid */}
                    <div className="grid grid-cols-2 gap-2 bg-slate-950/60 p-2 rounded-xl border border-slate-800/80">
                      {/* Top */}
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                          <span className="flex items-center gap-1 font-medium">⬆ Top</span>
                          <span className="font-mono text-slate-200">{canvasConfig.paddingTop || 0}px</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={200}
                          value={canvasConfig.paddingTop || 0}
                          onChange={(e) => handleSetSidePadding("paddingTop", Number(e.target.value))}
                          className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>

                      {/* Right */}
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                          <span className="flex items-center gap-1 font-medium">➡ Right</span>
                          <span className="font-mono text-slate-200">{canvasConfig.paddingRight || 0}px</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={200}
                          value={canvasConfig.paddingRight || 0}
                          onChange={(e) => handleSetSidePadding("paddingRight", Number(e.target.value))}
                          className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>

                      {/* Bottom */}
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                          <span className="flex items-center gap-1 font-medium">⬇ Bottom</span>
                          <span className="font-mono text-slate-200">{canvasConfig.paddingBottom || 0}px</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={260}
                          value={canvasConfig.paddingBottom || 0}
                          onChange={(e) => handleSetSidePadding("paddingBottom", Number(e.target.value))}
                          className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>

                      {/* Left */}
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                          <span className="flex items-center gap-1 font-medium">⬅ Left</span>
                          <span className="font-mono text-slate-200">{canvasConfig.paddingLeft || 0}px</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={200}
                          value={canvasConfig.paddingLeft || 0}
                          onChange={(e) => handleSetSidePadding("paddingLeft", Number(e.target.value))}
                          className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Presets */}
                <div className="flex items-center gap-1 flex-wrap pt-0.5">
                  <span className="text-[10px] text-slate-500">Presets:</span>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("none")}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                  >
                    0px
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("uniform24")}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                  >
                    24px
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("uniform48")}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                  >
                    48px
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("polaroid")}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-amber-300 transition"
                    title="Polaroid matting (Bottom 140px)"
                  >
                    Polaroid
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("banner")}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-cyan-300 transition"
                    title="Banner matting (Bottom 110px)"
                  >
                    Banner
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Frames & Borders Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Frames & Borders
              </h3>
              <span className="text-[10px] bg-[#3a57e8]/20 text-[#3a57e8] border border-[#3a57e8]/30 px-2 py-0.5 rounded-full font-medium">
                {canvasConfig.frame?.type && canvasConfig.frame?.type !== "NONE"
                  ? FRAME_PRESETS.find((f) => f.id === canvasConfig.frame?.type)?.label || canvasConfig.frame?.type
                  : "No Frame"}
              </span>
            </div>

            {/* Preset Frame Options Grid */}
            <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto pr-1 p-1 bg-slate-900/60 rounded-xl border border-slate-800 mb-3">
              {FRAME_PRESETS.map((fp) => {
                const isSelected = (canvasConfig.frame?.type || "NONE") === fp.id;
                return (
                  <button
                    key={fp.id}
                    onClick={() => handleSelectFrame(fp.id)}
                    className={`p-2 rounded-lg text-left transition flex items-center gap-2 border ${
                      isSelected
                        ? "bg-[#3a57e8] text-white border-transparent shadow-md"
                        : "bg-slate-800/60 hover:bg-slate-800 text-slate-300 border-slate-700/40"
                    }`}
                  >
                    <span className="text-base">{fp.icon}</span>
                    <div className="truncate">
                      <div className="text-[11px] font-semibold truncate leading-tight">{fp.label}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Frame Settings when a frame is active */}
            {canvasConfig.frame?.type && canvasConfig.frame?.type !== "NONE" && (
              <div className="space-y-3 bg-slate-900/60 p-3 rounded-xl border border-slate-800">
                {/* Frame Placement: IN THE IMAGE vs OUTSIDE OF THE IMAGE */}
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1.5 font-medium">
                    Frame Placement
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={() => handleFramePositionChange("INSIDE")}
                      className={`py-2 px-2 rounded-lg text-[11px] font-semibold transition border flex flex-col items-center justify-center text-center ${
                        (canvasConfig.frame?.position || "INSIDE") === "INSIDE"
                          ? "bg-[#3a57e8] text-white border-transparent shadow-sm"
                          : "bg-slate-800 text-slate-300 border-slate-700/60 hover:bg-slate-700"
                      }`}
                    >
                      <span className="text-sm mb-0.5">🎯</span>
                      <span>In the Image</span>
                      <span className="text-[9px] opacity-75 font-normal">(Overlay on image)</span>
                    </button>

                    <button
                      onClick={() => handleFramePositionChange("OUTSIDE")}
                      className={`py-2 px-2 rounded-lg text-[11px] font-semibold transition border flex flex-col items-center justify-center text-center ${
                        canvasConfig.frame?.position === "OUTSIDE"
                          ? "bg-[#3a57e8] text-white border-transparent shadow-sm"
                          : "bg-slate-800 text-slate-300 border-slate-700/60 hover:bg-slate-700"
                      }`}
                    >
                      <span className="text-sm mb-0.5">🖼️</span>
                      <span>Outside Image</span>
                      <span className="text-[9px] opacity-75 font-normal">(Canvas outer frame)</span>
                    </button>
                  </div>
                </div>

                {/* Frame Dimensions & Adjustments (Width Scale, Height Scale, X/Y Offset) */}
                <div className="bg-slate-950/60 p-2.5 rounded-xl border border-slate-800 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-semibold text-slate-300">Frame Size & Adjust</span>
                      <button
                        type="button"
                        onClick={handleToggleScaleLinked}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition flex items-center gap-0.5 ${
                          canvasConfig.frame?.isScaleLinked !== false
                            ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                            : "bg-slate-800 text-slate-400 hover:text-white"
                        }`}
                        title={canvasConfig.frame?.isScaleLinked !== false ? "Width & Height Scale Linked" : "Width & Height Scale Independent"}
                      >
                        <span>{canvasConfig.frame?.isScaleLinked !== false ? "🔗 Proportional" : "🔓 Free"}</span>
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleResetFrameAdjustments}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
                      title="Reset frame scale to 100% and offsets to 0"
                    >
                      ↺ Reset
                    </button>
                  </div>

                  {/* Frame Width Scale */}
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-slate-400 mb-0.5">
                      <span>Width Scale: {canvasConfig.frame?.scaleX || 100}%</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={10}
                          max={250}
                          value={canvasConfig.frame?.scaleX || 100}
                          onChange={(e) => handleSetFrameScale("x", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-[11px] font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">%</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={20}
                      max={180}
                      value={canvasConfig.frame?.scaleX || 100}
                      onChange={(e) => handleSetFrameScale("x", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Frame Height Scale */}
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-slate-400 mb-0.5">
                      <span>Height Scale: {canvasConfig.frame?.scaleY || 100}%</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={10}
                          max={250}
                          value={canvasConfig.frame?.scaleY || 100}
                          onChange={(e) => handleSetFrameScale("y", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-[11px] font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">%</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={20}
                      max={180}
                      value={canvasConfig.frame?.scaleY || 100}
                      onChange={(e) => handleSetFrameScale("y", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Frame Position X Offset */}
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-slate-400 mb-0.5">
                      <span>X Offset: {canvasConfig.frame?.offsetX || 0}px</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={-400}
                          max={400}
                          value={canvasConfig.frame?.offsetX || 0}
                          onChange={(e) => handleSetFrameOffset("x", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-[11px] font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">px</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={-250}
                      max={250}
                      value={canvasConfig.frame?.offsetX || 0}
                      onChange={(e) => handleSetFrameOffset("x", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Frame Position Y Offset */}
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-slate-400 mb-0.5">
                      <span>Y Offset: {canvasConfig.frame?.offsetY || 0}px</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={-400}
                          max={400}
                          value={canvasConfig.frame?.offsetY || 0}
                          onChange={(e) => handleSetFrameOffset("y", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-[11px] font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">px</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={-250}
                      max={250}
                      value={canvasConfig.frame?.offsetY || 0}
                      onChange={(e) => handleSetFrameOffset("y", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Alignment Buttons */}
                  <div className="flex items-center gap-1.5 pt-1">
                    <button
                      type="button"
                      onClick={() => handleCenterFrame("x")}
                      className="flex-1 py-1 text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 rounded border border-slate-700 transition"
                    >
                      ↔ Center X
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCenterFrame("y")}
                      className="flex-1 py-1 text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 rounded border border-slate-700 transition"
                    >
                      ↕ Center Y
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCenterFrame("both")}
                      className="flex-1 py-1 text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300 rounded border border-slate-700 transition"
                    >
                      🎯 Center
                    </button>
                  </div>
                </div>

                {/* Frame Thickness */}
                {canvasConfig.frame?.type !== "CUSTOM_PNG" && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[11px] text-slate-400">
                        Frame Thickness: {canvasConfig.frame?.width || 24}px
                      </label>
                    </div>
                    <input
                      type="range"
                      min={4}
                      max={70}
                      value={canvasConfig.frame?.width || 24}
                      onChange={(e) => updateFrameConfig("width", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                )}

                {/* Colors */}
                {canvasConfig.frame?.type !== "CUSTOM_PNG" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Primary Color</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={canvasConfig.frame?.color || "#b91c1c"}
                          onChange={(e) => updateFrameConfig("color", e.target.value)}
                          className="w-7 h-7 rounded cursor-pointer bg-transparent border-0"
                        />
                        <input
                          type="text"
                          value={canvasConfig.frame?.color || "#b91c1c"}
                          onChange={(e) => updateFrameConfig("color", e.target.value)}
                          className="w-full text-[11px] bg-slate-800 border border-slate-700 rounded px-1.5 py-1 text-white font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Accent Color</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={canvasConfig.frame?.accentColor || "#f59e0b"}
                          onChange={(e) => updateFrameConfig("accentColor", e.target.value)}
                          className="w-7 h-7 rounded cursor-pointer bg-transparent border-0"
                        />
                        <input
                          type="text"
                          value={canvasConfig.frame?.accentColor || "#f59e0b"}
                          onChange={(e) => updateFrameConfig("accentColor", e.target.value)}
                          className="w-full text-[11px] bg-slate-800 border border-slate-700 rounded px-1.5 py-1 text-white font-mono"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Color Palette */}
                {canvasConfig.frame?.type !== "CUSTOM_PNG" && (
                  <div className="flex items-center gap-1.5 pt-1">
                    <span className="text-[10px] text-slate-500">Quick:</span>
                    {[
                      { c: "#ffffff", name: "White" },
                      { c: "#0f172a", name: "Dark" },
                      { c: "#b91c1c", name: "Red" },
                      { c: "#f59e0b", name: "Gold" },
                      { c: "#3a57e8", name: "Blue" },
                      { c: "#10b981", name: "Emerald" },
                    ].map((pal) => (
                      <button
                        key={pal.c}
                        onClick={() => updateFrameConfig("color", pal.c)}
                        className="w-5 h-5 rounded-full border border-slate-600 hover:scale-110 transition shadow-sm"
                        style={{ backgroundColor: pal.c }}
                        title={pal.name}
                      />
                    ))}
                  </div>
                )}

                {/* Custom PNG Upload controls */}
                {canvasConfig.frame?.type === "CUSTOM_PNG" && (
                  <div className="pt-1">
                    {canvasConfig.frame?.customUrl ? (
                      <div className="relative group rounded-lg overflow-hidden border border-slate-700">
                        <img
                          src={resolveImageUrl(canvasConfig.frame.customUrl)}
                          alt="Custom Frame"
                          className="w-full h-16 object-contain bg-slate-950 p-1"
                        />
                        <button
                          onClick={() => updateFrameConfig("customUrl", "")}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded p-1 text-[10px]"
                          title="Remove custom frame"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => customFrameFileInputRef.current?.click()}
                        className="w-full py-2 px-3 border border-dashed border-slate-700 hover:border-[#3a57e8] rounded-lg text-xs text-slate-300 hover:text-white transition flex items-center justify-center gap-1.5 bg-slate-800/40"
                      >
                        <span>🖼️</span>
                        <span>Upload PNG Frame</span>
                      </button>
                    )}
                    <input
                      ref={customFrameFileInputRef}
                      type="file"
                      accept="image/png,image/webp,image/*"
                      onChange={handleCustomFrameUpload}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Variables Cheatsheet */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Dynamic Variables
              </h3>
              <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded font-mono">
                {"{{var}}"}
              </span>
            </div>
            <p className="text-[11px] text-slate-400 mb-2 leading-relaxed">
              Click to insert into the currently selected text layer:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {COMMON_VARIABLES.map((v) => (
                <button
                  key={v}
                  onClick={() => handleInsertVariable(v)}
                  className="px-2 py-1 text-[11px] rounded-md bg-slate-800 hover:bg-[#3a57e8] text-slate-300 hover:text-white border border-slate-700/60 font-mono transition"
                >
                  +{v}
                </button>
              ))}
            </div>
          </div>

          {/* Layers Stack List */}
          <div className="flex-1 flex flex-col min-h-0">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Layer Stack ({layers.length})
            </h3>
            <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
              {[...layers].reverse().map((layer) => (
                <div
                  key={layer.id}
                  onClick={() => setSelectedLayerId(layer.id)}
                  className={`flex items-center justify-between px-2.5 py-2 rounded-lg text-xs cursor-pointer transition border ${
                    selectedLayerId === layer.id
                      ? "bg-[#3a57e8] text-white border-transparent shadow-sm"
                      : "bg-slate-800/60 hover:bg-slate-800 text-slate-300 border-slate-700/50"
                  }`}
                >
                  <div className="flex items-center gap-2 truncate">
                    <span>{layer.type === "text" ? "T" : "🖼️"}</span>
                    <span className="truncate font-medium">{layer.name}</span>
                  </div>
                  <div className="flex items-center gap-1 opacity-80">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setLayers((prev) =>
                          prev.map((l) =>
                            l.id === layer.id ? { ...l, visible: !l.visible } : l
                          )
                        );
                      }}
                      className="p-1 hover:text-white"
                      title={layer.visible === false ? "Hidden" : "Visible"}
                    >
                      {layer.visible === false ? "🚫" : "👁️"}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteLayer(layer.id);
                      }}
                      className="p-1 hover:text-red-300"
                      title="Delete Layer"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CENTER COLUMN: Interactive Canvas Stage */}
        <div
          ref={containerRef}
          className="flex-1 bg-[#0b0f19] flex flex-col items-center justify-center p-6 relative overflow-auto select-none"
        >
          {/* Zoom & Reset Toolbar */}
          <div className="absolute top-4 right-4 bg-slate-900/90 backdrop-blur border border-slate-800 rounded-xl p-1 flex items-center gap-2 shadow-lg z-10">
            <button
              onClick={() => setZoom((prev) => Math.max(0.15, Number((prev - 0.05).toFixed(2))))}
              className="px-2 py-1 text-xs text-slate-300 hover:text-white hover:bg-slate-800 rounded transition"
            >
              -
            </button>
            <span className="text-xs font-mono text-slate-400 w-12 text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom((prev) => Math.min(1.5, Number((prev + 0.05).toFixed(2))))}
              className="px-2 py-1 text-xs text-slate-300 hover:text-white hover:bg-slate-800 rounded transition"
            >
              +
            </button>
            <button
              onClick={() => setZoom(0.5)}
              className="px-2 py-1 text-[11px] text-blue-400 hover:bg-blue-500/10 rounded transition font-medium"
            >
              Reset
            </button>
          </div>

          {/* Canvas Container with scaled dimensions */}
          <div
            className="relative shadow-2xl rounded-lg overflow-hidden border border-slate-700/80 cursor-crosshair transition-transform"
            style={{
              width: canvasConfig.width * zoom,
              height: canvasConfig.height * zoom,
            }}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
          >
            {/* The actual full-resolution HTML5 Canvas */}
            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 origin-top-left"
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: "top left",
              }}
            />

            {/* Interactive Overlay Box for Selected Layer */}
            {selectedLayer && selectedLayer.visible !== false && (
              <div
                className="absolute border-2 border-blue-400 pointer-events-none rounded"
                style={{
                  left: (selectedLayer.x || 0) * zoom,
                  top: (selectedLayer.y || 0) * zoom,
                  width:
                    selectedLayer.type === "text"
                      ? ((selectedLayer.maxWidth || 400) * zoom)
                      : ((selectedLayer.width || 200) * zoom),
                  height:
                    selectedLayer.type === "text"
                      ? ((selectedLayer.lineHeight || 60) * 1.5 * zoom)
                      : ((selectedLayer.height || 200) * zoom),
                }}
              >
                <div className="absolute -top-6 left-0 bg-[#3a57e8] text-white text-[10px] font-mono font-bold px-1.5 py-0.5 rounded shadow">
                  {selectedLayer.name} ({selectedLayer.x}, {selectedLayer.y})
                </div>
              </div>
            )}
          </div>

          <div className="mt-3 text-xs text-slate-500 flex items-center gap-3">
            <span>💡 Tip: Click on canvas to drag and position active layer</span>
            <span>•</span>
            <span>Use test button to preview real user values</span>
          </div>
        </div>

        {/* RIGHT COLUMN: Layer Properties Inspector */}
        <div className="w-80 border-l border-slate-800 bg-[#1e293b]/70 p-4 flex flex-col gap-4 overflow-y-auto">
          {selectedLayer ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">
                    {selectedLayer.type === "text" ? "✍️" : "🖼️"}
                  </span>
                  <input
                    type="text"
                    value={selectedLayer.name}
                    onChange={(e) => updateSelectedLayer("name", e.target.value)}
                    className="bg-transparent border-b border-transparent hover:border-slate-700 text-sm font-semibold text-white focus:outline-none"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleDuplicateLayer}
                    className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800"
                    title="Duplicate Layer"
                  >
                    📄
                  </button>
                  <button
                    onClick={() => handleDeleteLayer(selectedLayer.id)}
                    className="p-1 rounded text-red-400 hover:text-red-300 hover:bg-slate-800"
                    title="Delete Layer"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* TEXT PROPERTIES */}
              {selectedLayer.type === "text" && (
                <div className="space-y-3.5">
                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">
                      Text Content (Supports {"{{variable}}"})
                    </label>
                    <textarea
                      rows={3}
                      value={selectedLayer.text}
                      onChange={(e) => updateSelectedLayer("text", e.target.value)}
                      className="w-full text-xs bg-slate-900 border border-slate-700 rounded-lg p-2 text-white focus:border-[#3a57e8] focus:outline-none font-mono"
                    />
                  </div>

                  {/* Font Family & Weight */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Font Family</label>
                      <select
                        value={selectedLayer.fontFamily || "Inter"}
                        onChange={(e) => updateSelectedLayer("fontFamily", e.target.value)}
                        className="w-full text-xs bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-white focus:outline-none"
                      >
                        {FONT_OPTIONS.map((f) => (
                          <option key={f.value} value={f.value}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Weight</label>
                      <select
                        value={selectedLayer.fontWeight || 600}
                        onChange={(e) => updateSelectedLayer("fontWeight", Number(e.target.value))}
                        className="w-full text-xs bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-white focus:outline-none"
                      >
                        <option value={400}>Regular (400)</option>
                        <option value={500}>Medium (500)</option>
                        <option value={600}>Semi-Bold (600)</option>
                        <option value={700}>Bold (700)</option>
                        <option value={800}>Extra Bold (800)</option>
                        <option value={900}>Black (900)</option>
                      </select>
                    </div>
                  </div>

                  {/* Font Size & Line Height */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">
                        Font Size: {selectedLayer.fontSize}px
                      </label>
                      <input
                        type="range"
                        min={14}
                        max={160}
                        value={selectedLayer.fontSize}
                        onChange={(e) =>
                          updateSelectedLayer("fontSize", Number(e.target.value))
                        }
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">
                        Line Height: {selectedLayer.lineHeight}px
                      </label>
                      <input
                        type="number"
                        value={selectedLayer.lineHeight || 60}
                        onChange={(e) =>
                          updateSelectedLayer("lineHeight", Number(e.target.value))
                        }
                        className="w-full text-xs bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-white font-mono"
                      />
                    </div>
                  </div>

                  {/* Color & Alignment */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Text Color</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={selectedLayer.color || "#ffffff"}
                          onChange={(e) => updateSelectedLayer("color", e.target.value)}
                          className="w-7 h-7 rounded cursor-pointer bg-transparent border-0"
                        />
                        <input
                          type="text"
                          value={selectedLayer.color || "#ffffff"}
                          onChange={(e) => updateSelectedLayer("color", e.target.value)}
                          className="flex-1 text-xs bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Align</label>
                      <div className="flex items-center bg-slate-900 rounded-lg p-0.5 border border-slate-700">
                        {["left", "center", "right"].map((align) => (
                          <button
                            key={align}
                            onClick={() => updateSelectedLayer("textAlign", align)}
                            className={`flex-1 py-1 text-xs capitalize rounded ${
                              selectedLayer.textAlign === align
                                ? "bg-[#3a57e8] text-white"
                                : "text-slate-400 hover:text-white"
                            }`}
                          >
                            {align[0].toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Shadow / Outline */}
                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">Shadow Blur</label>
                    <input
                      type="range"
                      min={0}
                      max={40}
                      value={selectedLayer.shadowBlur || 0}
                      onChange={(e) =>
                        updateSelectedLayer("shadowBlur", Number(e.target.value))
                      }
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {/* IMAGE PROPERTIES */}
              {selectedLayer.type === "image" && (
                <div className="space-y-3.5">
                  {/* Image Preview & Replace File Button */}
                  <div className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-900 border border-slate-700">
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0">
                      {selectedLayer.url && !selectedLayer.url.startsWith("{{") ? (
                        <img
                          src={resolveImageUrl(selectedLayer.url)}
                          alt="Thumbnail"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const cur = e.target.src || "";
                            if (cur.includes("apis.digiforest.in")) {
                              e.target.src = cur.replace("https://apis.digiforest.in", "http://localhost:8005");
                            } else {
                              e.target.style.display = "none";
                            }
                          }}
                        />
                      ) : (
                        <span className="text-xl">🖼️</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => replaceFileInputRef.current?.click()}
                        disabled={uploadingOverlay}
                        className="w-full py-1.5 px-2.5 rounded-lg bg-[#3a57e8] hover:bg-blue-600 text-white text-xs font-semibold transition"
                      >
                        {uploadingOverlay ? "Uploading..." : "Replace Image File"}
                      </button>
                      <input
                        ref={replaceFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleReplaceImage}
                        className="hidden"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">
                      Image Source URL (Supports {"{{url_variable}}"})
                    </label>
                    <input
                      type="text"
                      value={selectedLayer.url}
                      onChange={(e) => updateSelectedLayer("url", e.target.value)}
                      className="w-full text-xs bg-slate-900 border border-slate-700 rounded-lg p-2 text-white font-mono"
                      placeholder="https://... or {{qr_code}}"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Width (px)</label>
                      <input
                        type="number"
                        value={selectedLayer.width || 200}
                        onChange={(e) =>
                          updateSelectedLayer("width", Number(e.target.value))
                        }
                        className="w-full text-xs bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Height (px)</label>
                      <input
                        type="number"
                        value={selectedLayer.height || 200}
                        onChange={(e) =>
                          updateSelectedLayer("height", Number(e.target.value))
                        }
                        className="w-full text-xs bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">
                      Corner Radius: {selectedLayer.borderRadius || 0}px
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={150}
                      value={selectedLayer.borderRadius || 0}
                      onChange={(e) =>
                        updateSelectedLayer("borderRadius", Number(e.target.value))
                      }
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">
                      Opacity: {selectedLayer.opacity !== undefined ? selectedLayer.opacity : 1}
                    </label>
                    <input
                      type="range"
                      min={0.1}
                      max={1}
                      step={0.05}
                      value={selectedLayer.opacity !== undefined ? selectedLayer.opacity : 1}
                      onChange={(e) =>
                        updateSelectedLayer("opacity", Number(e.target.value))
                      }
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {/* Coordinates (X, Y) */}
              <div className="border-t border-slate-800 pt-3">
                <label className="text-[11px] text-slate-400 block mb-1">Position (X, Y)</label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded px-2 py-1">
                    <span className="text-[10px] text-slate-500 font-mono">X:</span>
                    <input
                      type="number"
                      value={selectedLayer.x || 0}
                      onChange={(e) => updateSelectedLayer("x", Number(e.target.value))}
                      className="w-full text-xs bg-transparent text-white focus:outline-none font-mono"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded px-2 py-1">
                    <span className="text-[10px] text-slate-500 font-mono">Y:</span>
                    <input
                      type="number"
                      value={selectedLayer.y || 0}
                      onChange={(e) => updateSelectedLayer("y", Number(e.target.value))}
                      className="w-full text-xs bg-transparent text-white focus:outline-none font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Quick Center Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const layerWidth =
                      selectedLayer.type === "text"
                        ? selectedLayer.maxWidth || 400
                        : selectedLayer.width || 200;
                    updateSelectedLayer(
                      "x",
                      Math.round((canvasConfig.width - layerWidth) / 2)
                    );
                  }}
                  className="flex-1 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded border border-slate-700 transition"
                >
                  Center Horiz
                </button>
                <button
                  onClick={() => {
                    const layerHeight =
                      selectedLayer.type === "text"
                        ? selectedLayer.lineHeight || 60
                        : selectedLayer.height || 200;
                    updateSelectedLayer(
                      "y",
                      Math.round((canvasConfig.height - layerHeight) / 2)
                    );
                  }}
                  className="flex-1 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded border border-slate-700 transition"
                >
                  Center Vert
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border-b border-slate-800 pb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🖼️</span>
                  <span className="text-sm font-semibold text-white">Canvas & Frame Settings</span>
                </div>
                <span className="text-[11px] font-mono text-slate-400">
                  {canvasConfig.width}×{canvasConfig.height}
                </span>
              </div>

              {/* 4-Sided Margin & Padding Controls */}
              <div className="bg-slate-900/60 p-3 rounded-xl border border-slate-800 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-slate-300">Margin / Padding</span>
                    <button
                      type="button"
                      onClick={handleTogglePaddingLinked}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition flex items-center gap-1 ${
                        canvasConfig.isPaddingLinked !== false
                          ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                          : "bg-slate-800 text-slate-400 hover:text-white"
                      }`}
                      title={canvasConfig.isPaddingLinked !== false ? "Sides Linked (Click to Unlink)" : "Sides Independent (Click to Link)"}
                    >
                      <span>{canvasConfig.isPaddingLinked !== false ? "🔗 Linked" : "🔓 Independent"}</span>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveOutsidePadding}
                    className={`text-[11px] px-2.5 py-1 rounded-md transition font-medium ${
                      (canvasConfig.paddingTop || 0) === 0 &&
                      (canvasConfig.paddingRight || 0) === 0 &&
                      (canvasConfig.paddingBottom || 0) === 0 &&
                      (canvasConfig.paddingLeft || 0) === 0
                        ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold"
                        : "bg-slate-800 hover:bg-slate-700 text-slate-300"
                    }`}
                  >
                    {(canvasConfig.paddingTop || 0) === 0 &&
                    (canvasConfig.paddingRight || 0) === 0 &&
                    (canvasConfig.paddingBottom || 0) === 0 &&
                    (canvasConfig.paddingLeft || 0) === 0
                      ? "✓ 0px Bleed"
                      : "✕ 0px All"}
                  </button>
                </div>

                {/* Linked vs Independent Side Controls */}
                {canvasConfig.isPaddingLinked !== false ? (
                  <div>
                    <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                      <span>All Sides: {canvasConfig.padding || 0}px</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          max={250}
                          value={canvasConfig.padding || 0}
                          onChange={(e) => handleSetSidePadding("paddingTop", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-xs font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">px</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={180}
                      value={canvasConfig.padding || 0}
                      onChange={(e) => handleSetSidePadding("paddingTop", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                ) : (
                  <div className="space-y-2 pt-1">
                    <div className="grid grid-cols-2 gap-2 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800/80">
                      {/* Top */}
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                          <span className="flex items-center gap-1 font-medium">⬆ Top</span>
                          <span className="font-mono text-slate-200">{canvasConfig.paddingTop || 0}px</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={200}
                          value={canvasConfig.paddingTop || 0}
                          onChange={(e) => handleSetSidePadding("paddingTop", Number(e.target.value))}
                          className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>

                      {/* Right */}
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                          <span className="flex items-center gap-1 font-medium">➡ Right</span>
                          <span className="font-mono text-slate-200">{canvasConfig.paddingRight || 0}px</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={200}
                          value={canvasConfig.paddingRight || 0}
                          onChange={(e) => handleSetSidePadding("paddingRight", Number(e.target.value))}
                          className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>

                      {/* Bottom */}
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                          <span className="flex items-center gap-1 font-medium">⬇ Bottom</span>
                          <span className="font-mono text-slate-200">{canvasConfig.paddingBottom || 0}px</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={260}
                          value={canvasConfig.paddingBottom || 0}
                          onChange={(e) => handleSetSidePadding("paddingBottom", Number(e.target.value))}
                          className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>

                      {/* Left */}
                      <div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 mb-0.5">
                          <span className="flex items-center gap-1 font-medium">⬅ Left</span>
                          <span className="font-mono text-slate-200">{canvasConfig.paddingLeft || 0}px</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={200}
                          value={canvasConfig.paddingLeft || 0}
                          onChange={(e) => handleSetSidePadding("paddingLeft", Number(e.target.value))}
                          className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Presets */}
                <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                  <span className="text-[10px] text-slate-500">Presets:</span>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("none")}
                    className="text-[10px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                  >
                    0px
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("uniform24")}
                    className="text-[10px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                  >
                    24px
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("uniform48")}
                    className="text-[10px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 transition"
                  >
                    48px
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("polaroid")}
                    className="text-[10px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-amber-300 transition"
                    title="Polaroid matting (Bottom 140px)"
                  >
                    Polaroid
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMarginPreset("banner")}
                    className="text-[10px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-cyan-300 transition"
                    title="Banner matting (Bottom 110px)"
                  >
                    Banner
                  </button>
                </div>
              </div>

              {/* Frame Placement: IN THE IMAGE vs OUTSIDE OF THE IMAGE */}
              <div className="bg-slate-900/60 p-3 rounded-xl border border-slate-800 space-y-2.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-300">
                    Frame Placement
                  </label>
                  <span className="text-[10px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded font-mono">
                    {canvasConfig.frame?.position || "INSIDE"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleFramePositionChange("INSIDE")}
                    className={`p-2.5 rounded-lg text-xs font-semibold transition border flex flex-col items-center justify-center text-center ${
                      (canvasConfig.frame?.position || "INSIDE") === "INSIDE"
                        ? "bg-[#3a57e8] text-white border-transparent shadow-md"
                        : "bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700"
                    }`}
                  >
                    <span className="text-base mb-1">🎯</span>
                    <span>In the Image</span>
                    <span className="text-[9px] opacity-75 font-normal">(Overlay on image)</span>
                  </button>

                  <button
                    onClick={() => handleFramePositionChange("OUTSIDE")}
                    className={`p-2.5 rounded-lg text-xs font-semibold transition border flex flex-col items-center justify-center text-center ${
                      canvasConfig.frame?.position === "OUTSIDE"
                        ? "bg-[#3a57e8] text-white border-transparent shadow-md"
                        : "bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700"
                    }`}
                  >
                    <span className="text-base mb-1">🖼️</span>
                    <span>Outside Image</span>
                    <span className="text-[9px] opacity-75 font-normal">(Canvas outer frame)</span>
                  </button>
                </div>
              </div>

              {/* Frame Dimensions & Adjustments */}
              {canvasConfig.frame?.type && canvasConfig.frame?.type !== "NONE" && (
                <div className="bg-slate-900/60 p-3 rounded-xl border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-slate-300">Frame Size & Adjust</span>
                      <button
                        type="button"
                        onClick={handleToggleScaleLinked}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition flex items-center gap-0.5 ${
                          canvasConfig.frame?.isScaleLinked !== false
                            ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                            : "bg-slate-800 text-slate-400 hover:text-white"
                        }`}
                        title={canvasConfig.frame?.isScaleLinked !== false ? "Width & Height Scale Linked" : "Width & Height Scale Independent"}
                      >
                        <span>{canvasConfig.frame?.isScaleLinked !== false ? "🔗 Proportional" : "🔓 Free"}</span>
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleResetFrameAdjustments}
                      className="text-[10px] px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
                      title="Reset frame scale to 100% and offsets to 0"
                    >
                      ↺ Reset
                    </button>
                  </div>

                  {/* Frame Width Scale */}
                  <div>
                    <div className="flex items-center justify-between text-xs text-slate-400 mb-0.5">
                      <span>Width Scale: {canvasConfig.frame?.scaleX || 100}%</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={10}
                          max={250}
                          value={canvasConfig.frame?.scaleX || 100}
                          onChange={(e) => handleSetFrameScale("x", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-xs font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">%</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={20}
                      max={180}
                      value={canvasConfig.frame?.scaleX || 100}
                      onChange={(e) => handleSetFrameScale("x", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Frame Height Scale */}
                  <div>
                    <div className="flex items-center justify-between text-xs text-slate-400 mb-0.5">
                      <span>Height Scale: {canvasConfig.frame?.scaleY || 100}%</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={10}
                          max={250}
                          value={canvasConfig.frame?.scaleY || 100}
                          onChange={(e) => handleSetFrameScale("y", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-xs font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">%</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={20}
                      max={180}
                      value={canvasConfig.frame?.scaleY || 100}
                      onChange={(e) => handleSetFrameScale("y", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Frame Position X Offset */}
                  <div>
                    <div className="flex items-center justify-between text-xs text-slate-400 mb-0.5">
                      <span>X Offset: {canvasConfig.frame?.offsetX || 0}px</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={-400}
                          max={400}
                          value={canvasConfig.frame?.offsetX || 0}
                          onChange={(e) => handleSetFrameOffset("x", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-xs font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">px</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={-250}
                      max={250}
                      value={canvasConfig.frame?.offsetX || 0}
                      onChange={(e) => handleSetFrameOffset("x", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Frame Position Y Offset */}
                  <div>
                    <div className="flex items-center justify-between text-xs text-slate-400 mb-0.5">
                      <span>Y Offset: {canvasConfig.frame?.offsetY || 0}px</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={-400}
                          max={400}
                          value={canvasConfig.frame?.offsetY || 0}
                          onChange={(e) => handleSetFrameOffset("y", Number(e.target.value))}
                          className="w-14 text-right bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-white text-xs font-mono focus:border-[#3a57e8] focus:outline-none"
                        />
                        <span className="text-[10px] text-slate-500">px</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={-250}
                      max={250}
                      value={canvasConfig.frame?.offsetY || 0}
                      onChange={(e) => handleSetFrameOffset("y", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Alignment Buttons */}
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => handleCenterFrame("x")}
                      className="flex-1 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded border border-slate-700 transition"
                    >
                      ↔ Center X
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCenterFrame("y")}
                      className="flex-1 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded border border-slate-700 transition"
                    >
                      ↕ Center Y
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCenterFrame("both")}
                      className="flex-1 py-1 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded border border-slate-700 transition"
                    >
                      🎯 Center
                    </button>
                  </div>
                </div>
              )}

              {/* Frame Presets */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Select Frame Style
                  </label>
                  <span className="text-[10px] text-blue-400">
                    {FRAME_PRESETS.find((f) => f.id === (canvasConfig.frame?.type || "NONE"))?.label}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1">
                  {FRAME_PRESETS.map((fp) => {
                    const isSelected = (canvasConfig.frame?.type || "NONE") === fp.id;
                    return (
                      <button
                        key={fp.id}
                        onClick={() => handleSelectFrame(fp.id)}
                        className={`p-2 rounded-lg text-left transition flex items-center gap-2 border ${
                          isSelected
                            ? "bg-[#3a57e8] text-white border-transparent shadow-sm"
                            : "bg-slate-800/60 hover:bg-slate-800 text-slate-300 border-slate-700/40"
                        }`}
                      >
                        <span className="text-base">{fp.icon}</span>
                        <div className="truncate">
                          <div className="text-[11px] font-semibold truncate">{fp.label}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Frame Customization (when frame selected) */}
              {canvasConfig.frame?.type && canvasConfig.frame?.type !== "NONE" && canvasConfig.frame?.type !== "CUSTOM_PNG" && (
                <div className="bg-slate-900/60 p-3 rounded-xl border border-slate-800 space-y-3">
                  <div>
                    <label className="text-[11px] text-slate-400 block mb-1">
                      Thickness: {canvasConfig.frame?.width || 24}px
                    </label>
                    <input
                      type="range"
                      min={4}
                      max={70}
                      value={canvasConfig.frame?.width || 24}
                      onChange={(e) => updateFrameConfig("width", Number(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Color</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={canvasConfig.frame?.color || "#b91c1c"}
                          onChange={(e) => updateFrameConfig("color", e.target.value)}
                          className="w-7 h-7 rounded cursor-pointer bg-transparent border-0"
                        />
                        <input
                          type="text"
                          value={canvasConfig.frame?.color || "#b91c1c"}
                          onChange={(e) => updateFrameConfig("color", e.target.value)}
                          className="w-full text-xs bg-slate-800 border border-slate-700 rounded px-1.5 py-1 text-white font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 block mb-1">Accent</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={canvasConfig.frame?.accentColor || "#f59e0b"}
                          onChange={(e) => updateFrameConfig("accentColor", e.target.value)}
                          className="w-7 h-7 rounded cursor-pointer bg-transparent border-0"
                        />
                        <input
                          type="text"
                          value={canvasConfig.frame?.accentColor || "#f59e0b"}
                          onChange={(e) => updateFrameConfig("accentColor", e.target.value)}
                          className="w-full text-xs bg-slate-800 border border-slate-700 rounded px-1.5 py-1 text-white font-mono"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom PNG Upload controls in Right Panel */}
              {canvasConfig.frame?.type === "CUSTOM_PNG" && (
                <div className="bg-slate-900/60 p-3 rounded-xl border border-slate-800 space-y-2">
                  <label className="text-[11px] text-slate-400 block font-medium">Custom PNG Frame</label>
                  {canvasConfig.frame?.customUrl ? (
                    <div className="relative group rounded-lg overflow-hidden border border-slate-700">
                      <img
                        src={resolveImageUrl(canvasConfig.frame.customUrl)}
                        alt="Custom Frame"
                        className="w-full h-20 object-contain bg-slate-950 p-1"
                        onError={(e) => {
                          const cur = e.target.src || "";
                          if (cur.includes("apis.digiforest.in")) {
                            e.target.src = cur.replace("https://apis.digiforest.in", "http://localhost:8005");
                          } else {
                            e.target.style.display = "none";
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => updateFrameConfig("customUrl", "")}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded p-1 text-[10px]"
                        title="Remove custom frame"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => customFrameFileInputRef.current?.click()}
                      className="w-full py-2.5 px-3 border border-dashed border-slate-700 hover:border-[#3a57e8] rounded-lg text-xs text-slate-300 hover:text-white transition flex items-center justify-center gap-1.5 bg-slate-800/40"
                    >
                      <span>🖼️</span>
                      <span>Upload Transparent PNG Frame</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* TEST VARIABLES PREVIEW MODAL */}
      {showTestModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#1e293b] border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">⚡</span>
                <h3 className="text-base font-bold text-white">
                  Test Variables Live Preview
                </h3>
              </div>
              <button
                onClick={() => setShowTestModal(false)}
                className="text-slate-400 hover:text-white p-1 text-lg"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 p-6 overflow-y-auto">
              {/* Left Column: Variable Inputs */}
              <div>
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                  Populate Dynamic Values
                </h4>
                {detectedVariables.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">
                    No variables like {"{{name}}"} detected in your layers. Add variables to your text to test personalization!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {detectedVariables.map((v) => (
                      <div key={v}>
                        <label className="text-xs text-blue-400 font-mono block mb-1">
                          {"{{"} {v} {"}}"}
                        </label>
                        <input
                          type="text"
                          value={testValues[v] || ""}
                          onChange={(e) => handleUpdateTestVal(v, e.target.value)}
                          className="w-full text-xs bg-slate-900 border border-slate-700 rounded-lg p-2 text-white focus:border-[#3a57e8] focus:outline-none"
                          placeholder={`Enter sample value for ${v}...`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right Column: Live Output Render */}
              <div className="flex flex-col items-center justify-center">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 self-start">
                  Rendered Result
                </h4>
                {testPreviewUrl ? (
                  <div className="relative group max-w-sm rounded-xl overflow-hidden border border-slate-700 shadow-xl bg-slate-900">
                    <img
                      src={testPreviewUrl}
                      alt="Rendered Test"
                      className="w-full h-auto object-contain"
                    />
                    <a
                      href={testPreviewUrl}
                      download={`test_${title.toLowerCase().replace(/\s+/g, "_")}.png`}
                      className="absolute bottom-3 right-3 bg-[#3a57e8] hover:bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow transition flex items-center gap-1.5"
                    >
                      <span>📥</span>
                      <span>Download</span>
                    </a>
                  </div>
                ) : (
                  <div className="text-slate-500 text-xs">Generating preview...</div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-slate-800 flex justify-end gap-2 bg-slate-900/50">
              <button
                onClick={() => setShowTestModal(false)}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicImageEditor;
